// ─── Centralized Pricing Configuration ────────────────────────────
// All plan data, comparison features, FAQ, and handoff logic live here.
// Update prices, limits, and features in ONE place.

const APP_URL = "https://app.sidestepads.com";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
const UTM_STORAGE_KEY = "sidestep_utm_params";
// Meta ad click id — forwarded on every app hand-off so the app can attribute
// the signup/checkout conversion even when an ad blocker stopped the pixel
// from writing its _fbc cookie. Captured alongside UTMs on landing.
const FBCLID_STORAGE_KEY = "sidestep_fbclid";

/**
 * Capture utm_* params (and a Meta fbclid) from the current URL into
 * sessionStorage. Call once on app landing so they survive client-side
 * navigation.
 */
export function captureUtmParams(): void {
  if (typeof window === "undefined") return;
  try {
    const current = new URLSearchParams(window.location.search);
    const captured: Record<string, string> = {};
    UTM_KEYS.forEach((key) => {
      const value = current.get(key);
      if (value) captured[key] = value;
    });
    if (Object.keys(captured).length > 0) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(captured));
    }
    const fbclid = current.get("fbclid");
    if (fbclid && fbclid.length <= 400) {
      sessionStorage.setItem(FBCLID_STORAGE_KEY, fbclid);
    }
  } catch {
    // sessionStorage may be unavailable (privacy mode, SSR) — ignore
  }
}

function getStoredUtmParams(): Record<string, string> {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/** Current-URL fbclid first (freshest click), then the landing capture. */
function getCapturedFbclid(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const current = new URLSearchParams(window.location.search).get("fbclid");
    if (current && current.length <= 400) return current;
    return sessionStorage.getItem(FBCLID_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Append stored/current UTMs + fbclid to a hand-off URL's params. */
function appendTrackingParams(params: URLSearchParams): void {
  // Forward UTMs: stored (captured on landing) first, then current URL overrides.
  const stored = getStoredUtmParams();
  UTM_KEYS.forEach((key) => {
    if (stored[key]) params.set(key, stored[key]);
  });
  if (typeof window !== "undefined") {
    const currentParams = new URLSearchParams(window.location.search);
    UTM_KEYS.forEach((key) => {
      const value = currentParams.get(key);
      if (value) params.set(key, value);
    });
  }
  const fbclid = getCapturedFbclid();
  if (fbclid) params.set("fbclid", fbclid);
}

/**
 * Trial-signup URL for the generic "Start Free Trial" CTAs (header, hero,
 * bottom CTA, feature pages). Forwards captured UTMs + fbclid so the app can
 * attribute the signup conversion. Pricing plan cards use buildHandoffUrl.
 */
export function buildTrialSignupUrl(): string {
  const params = new URLSearchParams();
  params.set("mode", "signup");
  appendTrackingParams(params);
  return `${APP_URL}/login?${params.toString()}`;
}

/**
 * Sign-in URL for the header's "Sign In" link. The app's /login page carries
 * the trial-signup toggle, so an ad-click visitor who signs up from there
 * must not lose attribution — forward the captured UTMs + fbclid the same
 * way the conversion CTAs do. No `mode` param: this is the sign-in lane.
 */
export function buildLoginUrl(): string {
  const params = new URLSearchParams();
  appendTrackingParams(params);
  const query = params.toString();
  return query ? `${APP_URL}/login?${query}` : `${APP_URL}/login`;
}

// ─── Offer Terms ──────────────────────────────────────────────────
// The trial and guarantee facts all marketing copy must stay in sync with.
// The guarantee covers a FIRST-TIME customer's FIRST subscription payment
// only, and its window runs from that payment (see src/pages/RefundPolicy.tsx)
// — it does not simply extend the trial (a customer can subscribe before or
// well after the trial ends), and it does not renew for resubscribers.
export const TRIAL_DAYS = 14;
export const TRIAL_SHORT = `${TRIAL_DAYS}-day free trial`;
// The plan tier a trial unlocks. Marketing copy that says "full Pro access"
// should read this rather than hard-code the name.
export const TRIAL_PLAN_NAME = "Pro";
export const GUARANTEE_DAYS = 30;
export const GUARANTEE_SHORT = `${GUARANTEE_DAYS}-day money-back guarantee`;

// The recurring-billing terms shown next to the plan cards (and in the crawler
// HTML), so the auto-renewal and how to cancel are clear before signup.
export const RENEWAL_TERMS =
  `Paid plans renew automatically each month or year until you cancel. Cancel anytime in Billing. ` +
  `A first-time customer's first payment is refundable for ${GUARANTEE_DAYS} days.`;

// AI credit top-up packs, bought in the app, expire this many days after
// purchase (app repo: verify-topup / TopupPacksSection). Monthly plan credits
// reset each calendar month and do not roll over.
export const TOPUP_VALIDITY_DAYS = 90;

// The date the prices below were last confirmed against Stripe. Shown on the
// page ("Prices as of …") and in the crawler HTML; update it with any price
// change.
export const PRICES_AS_OF = "2026-10-12";
/** "October 12, 2026" (fixed to UTC so the date never shifts by time zone). */
export const PRICES_AS_OF_LABEL = new Date(`${PRICES_AS_OF}T00:00:00Z`).toLocaleDateString("en-US", {
  year: "numeric",
  month: "long",
  day: "numeric",
  timeZone: "UTC",
});

// ─── Types ────────────────────────────────────────────────────────

// The headline numbers buyers compare between plans (accounts, seats, AI
// credits), derived from each plan's numeric fields below.
export interface PlanLimit {
  label: string;
  value: string;
}

export interface Plan {
  id: "starter" | "pro" | "agency";
  name: string;
  monthlyPrice: number;
  /**
   * What a yearly subscription actually charges: the yearly list price
   * (12 × monthly) less the 20% coupon Stripe applies at checkout. Keep the
   * coupon in Stripe so the discount shows at checkout; keep this in cents
   * precision so the page shows the real charge, not a rounded one.
   */
  annualPrice: number;
  badge?: string;
  bestFor: string;
  highlighted: boolean;
  /** Workspace total across Meta + Google Ads combined, not per platform. */
  connectedAccounts: number;
  /** Includes the workspace owner. */
  seats: number;
  aiCreditsPerMonth: number;
  adsPerBulkPublish: number;
  adsPer24h: number;
  limits: PlanLimit[];
}

export type BillingCycle = "monthly" | "annual";

export interface FAQItem {
  question: string;
  answer: string;
}

// ─── Helpers ──────────────────────────────────────────────────────

const toCents = (n: number) => Math.round(n * 100) / 100;

/** "$39", "$31.20", "$3,830.40": whole dollars stay whole, otherwise cents. */
export function formatUsd(n: number): string {
  const v = toCents(n);
  return Number.isInteger(v)
    ? `$${v.toLocaleString("en-US")}`
    : `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Per-month price for the cycle: yearly is the yearly charge over 12. */
export function monthlyEquivalent(plan: Plan, cycle: BillingCycle): number {
  return cycle === "annual" ? toCents(plan.annualPrice / 12) : plan.monthlyPrice;
}

export function formatPrice(plan: Plan, cycle: BillingCycle): string {
  return formatUsd(monthlyEquivalent(plan, cycle));
}

export function annualTotal(plan: Plan): string {
  return formatUsd(plan.annualPrice);
}

/** What a year costs on monthly billing minus the yearly charge. */
export function annualSavings(plan: Plan): string {
  return formatUsd(plan.monthlyPrice * 12 - plan.annualPrice);
}

const limitsFor = (p: Pick<Plan, "connectedAccounts" | "seats" | "aiCreditsPerMonth">): PlanLimit[] => [
  { label: "Connected ad accounts", value: p.connectedAccounts.toLocaleString("en-US") },
  { label: "Team seats", value: p.seats.toLocaleString("en-US") },
  { label: "AI credits / month", value: p.aiCreditsPerMonth.toLocaleString("en-US") },
];

// ─── Plans ────────────────────────────────────────────────────────
// Every plan includes every feature; plans differ only in the capacity
// numbers below (app repo: plan_config + plan_entitlements).
// public/og/pricing-og.jpg (the /pricing share image) bakes in each plan's
// monthly price, ad-account cap and seat count — remake it when they change.

type PlanInput = Omit<Plan, "limits">;

const PLAN_INPUTS: PlanInput[] = [
  {
    id: "starter",
    name: "Starter",
    monthlyPrice: 39,
    annualPrice: 374.4,
    bestFor: "Solo advertisers running their own accounts",
    highlighted: false,
    connectedAccounts: 3,
    seats: 1,
    aiCreditsPerMonth: 150,
    adsPerBulkPublish: 10,
    adsPer24h: 30,
  },
  {
    id: "pro",
    name: "Pro",
    monthlyPrice: 99,
    annualPrice: 950.4,
    badge: "Most Popular",
    bestFor: "Freelancers, consultants and growing teams",
    highlighted: true,
    connectedAccounts: 15,
    seats: 3,
    aiCreditsPerMonth: 600,
    adsPerBulkPublish: 50,
    adsPer24h: 150,
  },
  {
    id: "agency",
    name: "Agency",
    monthlyPrice: 399,
    annualPrice: 3830.4,
    bestFor: "Agencies running a full client roster",
    highlighted: false,
    connectedAccounts: 100,
    seats: 10,
    aiCreditsPerMonth: 3000,
    adsPerBulkPublish: 100,
    adsPer24h: 1000,
  },
];

export const PLANS: Plan[] = PLAN_INPUTS.map((p) => ({ ...p, limits: limitsFor(p) }));

// The largest batch any plan can publish. Marketing copy that says "up to N
// ads" should read this rather than hard-code a number, so the claim moves with
// the Agency limit instead of drifting to a lower tier's cap.
export const MAX_ADS_PER_BULK_PUBLISH = Math.max(...PLANS.map((p) => p.adsPerBulkPublish));

/** The largest plan's connected-account cap ("Have more than N ad accounts?"). */
export const MAX_CONNECTED_ACCOUNTS = Math.max(...PLANS.map((p) => p.connectedAccounts));

/** Lowest monthly list price ("Plans from $39/month"). */
export const STARTING_MONTHLY_PRICE = Math.min(...PLANS.map((p) => p.monthlyPrice));

/**
 * The yearly discount every plan gets, as a whole percent — the smallest
 * across plans, rounded down, so "Save N%" is never more than any plan saves.
 */
export const ANNUAL_SAVINGS_PERCENT = Math.floor(
  Math.min(...PLANS.map((p) => 1 - p.annualPrice / (p.monthlyPrice * 12))) * 100 + 1e-9
);

/**
 * The one-sentence answer to "How much does Sidestep Ads cost?", for crawlers
 * and answer engines (the crawler intro and the SoftwareApplication JSON-LD).
 * Not shown as a visible FAQ: the page itself leads with the prices.
 */
const byPrice = [...PLANS].sort((a, b) => a.monthlyPrice - b.monthlyPrice);
const cheapest = byPrice[0];
const priciest = byPrice[byPrice.length - 1];
const NUMBER_WORDS = ["no", "one", "two", "three", "four", "five", "six"];
// "Starter, Pro, or Agency": the plan names as a choice, in PLANS order.
const planChoice = PLANS.map((p) => p.name).join(", ").replace(/, ([^,]*)$/, PLANS.length > 2 ? ", or $1" : " or $1");
const planCount = `${NUMBER_WORDS[PLANS.length] ?? PLANS.length} ${PLANS.length === 1 ? "plan" : "plans"}`;
export const PRICE_SUMMARY =
  `Sidestep Ads costs ${formatUsd(cheapest.monthlyPrice)} to ${formatUsd(priciest.monthlyPrice)} a month, ` +
  `or ${formatPrice(cheapest, "annual")} to ${formatPrice(priciest, "annual")} a month billed yearly, ` +
  `across ${planCount}: ${PLANS.map((p) => p.name).join(", ").replace(/, ([^,]*)$/, " and $1")}. ` +
  `Every plan includes every feature, and every new account starts with a ${TRIAL_SHORT} with no credit card.`;

// ─── FAQ ──────────────────────────────────────────────────────────
// Pricing questions only; product questions live on the homepage and
// Features page. Also baked into the crawler HTML and the FAQPage JSON-LD.

export const FAQ_ITEMS: FAQItem[] = [
  {
    question: "Do all plans include every feature?",
    answer:
      "Yes. Every plan includes every feature. Plans differ only in capacity: connected ad accounts, team seats, ads per bulk publish, ads per 24 hours and monthly AI credits.",
  },
  {
    question: "How does the free trial work?",
    answer:
      `Every new account gets ${TRIAL_DAYS} days of full ${TRIAL_PLAN_NAME} access, with no credit card required. When the trial ends, choose ${planChoice} to keep going — your workspace and data stay as you left them.`,
  },
  {
    question: "How does the money-back guarantee work?",
    answer:
      `It's separate from the trial. A first-time customer's first subscription payment, on any plan, is covered for ${GUARANTEE_DAYS} days from the date of that payment, whether you subscribe during the trial or after it. Email us within that window for a full refund.`,
  },
  {
    question: "How do AI credits work?",
    answer:
      `Every AI feature draws from your plan's monthly allowance, which resets each month; unused credits don't roll over. Need more? Buy a top-up pack in the app. Top-ups last ${TOPUP_VALIDITY_DAYS} days.`,
  },
  {
    question: "Do Meta and Google accounts count toward the same limit?",
    answer:
      "Yes. Your plan's connected-account limit is a workspace total across Meta and Google Ads combined.",
  },
  {
    question: "Can I change plans or billing later?",
    answer:
      `Yes. Change your plan or billing period from Billing in the app. Yearly billing saves ${ANNUAL_SAVINGS_PERCENT}% compared with paying monthly.`,
  },
  {
    question: "What payment methods do you accept?",
    answer:
      "We accept all major credit cards including Visa, Mastercard, and American Express.",
  },
];

// ─── Handoff URL Builder ──────────────────────────────────────────

/**
 * Plan-card URL (app `/signup?plan=…`). Like buildTrialSignupUrl it starts the
 * 14-day trial: the app treats the plan as a preference and sends a new or
 * still-trialing account into the app (setup wizard). Only an account with no
 * active subscription (an expired trial) is taken to checkout for this plan,
 * and a paying one to Billing (app repo: SignupRedirect → UpgradeRedirect,
 * resolveTrialFirstDestination). Forwards UTMs + fbclid like the other CTAs.
 */
export function buildHandoffUrl(
  planId: Plan["id"],
  options?: {
    source?: string;
    returnTo?: string;
    billing?: BillingCycle;
  }
): string {
  const params = new URLSearchParams();

  params.set("plan", planId);
  if (options?.billing) {
    // Canonical wire value: monthly | yearly (annual → yearly).
    const cycle = options.billing === "annual" ? "yearly" : options.billing;
    params.set("billing_cycle", cycle);
  }
  if (options?.returnTo) params.set("return_to", options.returnTo);

  // Attribution
  params.set("entry_point", options?.source ?? "pricing_page");

  appendTrackingParams(params);

  return `${APP_URL}/signup?${params.toString()}`;
}

/**
 * Builds a contact/sales handoff URL for Agency plan.
 */
export function buildContactUrl(): string {
  return "/contact";
}

// ─── JSON-LD ──────────────────────────────────────────────────────

/** Fields every Offer in the pricing JSON-LD shares. */
const OFFER_COMMON = {
  url: "https://sidestepads.com/pricing",
  availability: "https://schema.org/InStock",
  seller: { "@type": "Organization", name: "Sidestep Ads", url: "https://sidestepads.com" },
};

export const PRICING_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://sidestepads.com" },
        { "@type": "ListItem", position: 2, name: "Pricing", item: "https://sidestepads.com/pricing" },
      ],
    },
    {
      "@type": "SoftwareApplication",
      name: "Sidestep Ads",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url: "https://sidestepads.com/pricing",
      description: PRICE_SUMMARY,
      offers: PLANS.flatMap((p) => [
        {
          "@type": "Offer",
          name: `${p.name} (monthly)`,
          price: p.monthlyPrice.toFixed(2),
          priceCurrency: "USD",
          ...OFFER_COMMON,
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: p.monthlyPrice.toFixed(2),
            priceCurrency: "USD",
            unitText: "MONTH",
          },
        },
        {
          "@type": "Offer",
          name: `${p.name} (yearly)`,
          price: p.annualPrice.toFixed(2),
          priceCurrency: "USD",
          ...OFFER_COMMON,
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: p.annualPrice.toFixed(2),
            priceCurrency: "USD",
            unitText: "YEAR",
          },
        },
      ]),
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    },
  ],
};
